import React, { useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { OnboardingWarningBanner } from '@confluence/banners/entry-points/OnboardingWarningBanner.tsx';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { ONBOARDING_STORAGE_ENFORCEMENT_BANNER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';

import { getOverLimitFormatted } from '../../storageLimits/storageLimits.ts';
import { BANNER_HEIGHT } from '../constants.ts';
import { useGetCurrentStorageUsage } from '../../hooks/useGetCurrentStorageUsage.ts';

import { SiteAdminDropdownMenu } from './SiteAdminDropdownMenu.tsx';
import { useBannerAnalytics } from './useBannerAnalytics.ts';
import { PlanConsentDeepLink } from './PlanConsentDeepLinkLoadable.tsx';
import { LearnMoreLink } from './LearnMoreLink.tsx';

const i18n = defineMessages({
	adminHardMessageV2: {
		id: 'storage-enforcement.banner.admin.hard.message.v2',
		defaultMessage:
			'Action required. You won’t be able to create in Confluence soon, unless you upgrade for more storage or delete <b>{number} GB</b> of files by {date}. <a>Upgrade to Standard</a>',
		description:
			'Description that tells the user that they are nearing their confluence storage limits.',
	},
	adminHardEnforceMessage: {
		id: 'storage-enforcement.banner.admin.hard.enforced.message',
		defaultMessage:
			'Action required. You can’t edit or create new content in Confluence until you upgrade for more storage or delete <b>{number} GB</b> GB of files. <a>Upgrade to Standard</a>',
		description:
			'Tells the user that they are over their confluence storage limits and can no longer create',
	},
	nthUserHardMessage: {
		id: 'storage-enforcement.banner.nth-user.hard.message',
		defaultMessage:
			'Action required. You won’t be able to create in Confluence soon unless you delete files by {date}. <a>Learn more</a>',
		description:
			"Description that tells the user that they are nearing their confluence storage limits. The 'date' will be in this format: 'August 15, 2023'.",
	},
	nthUserHardEnforceMessage: {
		id: 'storage-enforcement.banner.nth-user.hard.enforced.message',
		defaultMessage:
			'Action required. You can’t edit or create new content in Confluence until you delete files. <a>Learn more</a>',
		description: 'Tells user they are over their storage limit and can no longer create.',
	},
});

export const HardBanner = () => {
	const { formatMessage, formatDate } = useIntl();
	const { currentUsage, gracePeriodEndDate, isStorageEnforcementGracePeriodComplete } =
		useGetCurrentStorageUsage();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { fireLearnMoreEvent, fireScreenEvent } = useBannerAnalytics();

	// The date will always be set at this point because it was asserted higher.
	const date = formatDate(new Date(gracePeriodEndDate as string), {
		// todo get the date
		year: 'numeric',
		month: 'long',
		day: '2-digit',
	});
	const number = getOverLimitFormatted(currentUsage, ConfluenceEdition.FREE);

	useEffect(() => {
		fireScreenEvent();
	}, [fireScreenEvent]);

	const adminMessage = isStorageEnforcementGracePeriodComplete
		? i18n.adminHardEnforceMessage
		: i18n.adminHardMessageV2;

	const nthUserMessage = isStorageEnforcementGracePeriodComplete
		? i18n.nthUserHardEnforceMessage
		: i18n.nthUserHardMessage;

	const description = isSiteAdmin
		? formatMessage(adminMessage, {
				date,
				number,
				a: (chunks: React.ReactNode[]) => <PlanConsentDeepLink>{chunks}</PlanConsentDeepLink>,
				b: (...chunks: any) => <b>{chunks}</b>,
			})
		: formatMessage(nthUserMessage, {
				date,
				number,
				a: (chunks: React.ReactNode[]) => <LearnMoreLink>{chunks}</LearnMoreLink>,
			});

	return (
		<>
			<ExperienceSuccess name={ONBOARDING_STORAGE_ENFORCEMENT_BANNER_EXPERIENCE} />
			<OnboardingWarningBanner
				bannerHeight={BANNER_HEIGHT}
				description={description}
				rightSideComponent={
					isSiteAdmin && (
						<SiteAdminDropdownMenu onLearnMoreClick={fireLearnMoreEvent} showDismiss={false} />
					)
				}
			/>
		</>
	);
};
